import { instagramOutreachAvailability } from '../instagram-outreach';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchConnectors } from '../connectors';
import { fetchMcpConnections } from '../mcp';
import { useMyAgents } from '../my-agents';
import { queryKeys } from '../query-client';
import { isCoderClient, partitionConnectors } from './channels';
import { useAlfredInOutreachEnabled } from '../alfred-in-outreach-flag';
import { useWorkspaces } from '../workspaces';
import { useAgentConnections } from '../agent-connections';
import { useMobileAppInstalled } from '../mobile-app-status';
import { buildWorld, type MapWorld } from './map-layout';
import type { KnownNodes } from './map-events';

export interface MapWorldState {
  /** The world to draw. Present as soon as the ROSTER is known — the connectors fill in when they land
   *  (the map must never wait on a slow read to paint; see the performance rules in the spec). */
  world: MapWorld | null;
  known: KnownNodes;
  drawnAgentIds: string[];
  agentNames: Record<string, string>;
  rosterReady: boolean;
  /** Every channel and tool the world was built from, folded ones included — what a "+N" plate stands for. */
  items: { slug: string; label: string; logoUrl?: string }[];
  /** The channels in and the tools, unfolded and in order — the phone map's two trays (`Home3PhoneMap`), which
   *  fold them at three themselves (`visibleChips`) rather than taking the world's own fold. */
  channelsIn: { slug: string; label: string; logoUrl?: string }[];
  connectors: { slug: string; label: string; logoUrl?: string }[];
}

/** Builds the map's world from the roster and the connected connectors. Re-lays-out ONLY when either read
 *  changes identity — everything downstream keys off `world`'s identity to avoid re-rendering the scene. */
/** One identity, so the error branch does not rebuild the world on every render. */
const EMPTY_ROSTER: { id: string; name: string }[] = [];

/**
 * `withSpaces: false` builds the world with no Space districts at all — the canvas draws none. Leaving them
 * out of the BUILD (rather than hiding the nodes) also drops them from `known.spaces`, so an event that
 * would have landed on a Space falls back to its bucket (`destinationFor`) instead of flying to a node
 * that is not on the map.
 */
export function useMapWorld({ withSpaces = true }: { withSpaces?: boolean } = {}): MapWorldState {
  const agents = useMyAgents();
  const connectors = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 });
  const mcp = useQuery({ queryKey: queryKeys.homeMapMcp, queryFn: fetchMcpConnections, staleTime: 60_000 });

  // A FAILED roster read still draws a map (the Maestro, the brief, the buckets — the spec's brand-new
  // account shape) rather than parking the home on a spinner for as long as the agents service is down.
  // `useMyAgents` keeps a prior good team across a transient failure, so this only bites on a cold miss.
  const roster = agents.status === 'ready' ? agents.team.agents : agents.status === 'error' ? EMPTY_ROSTER : null;
  const instagramAvailability = instagramOutreachAvailability(connectors.data, connectors.error);
  const list = connectors.data?.connectors ?? null;
  const servers = mcp.data ?? null;
  // The AI-coder clients the user connected (Claude Code, Gemini…), one node per name, and the phone app.
  const { connections } = useAgentConnections();
  const phoneApp = useMobileAppInstalled();
  const coders = useMemo(() => {
    const byName = new Map<string, { id: string; label: string }>();
    // Most recently used first, so the column's cap keeps the clients that are actually in use.
    const sorted = [...connections].sort(
      (a, b) => new Date(b.lastUsedDate ?? b.connectedDate ?? 0).getTime() - new Date(a.lastUsedDate ?? a.connectedDate ?? 0).getTime(),
    );
    for (const c of sorted) {
      if (!isCoderClient(c)) continue;
      const label = (c.agentName || c.agentPlatform || '').trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (!byName.has(key)) byName.set(key, { id: c.id, label });
    }
    return [...byName.values()];
  }, [connections]);

  const workspaces = useWorkspaces();
  const spaces = useMemo(
    () =>
      withSpaces && workspaces.status === 'ready' ? workspaces.workspaces.map((w) => ({ id: w.id, slug: w.slug, name: w.name, icon: w.icon })) : [],
    [workspaces, withSpaces],
  );

  const alfredOnOutreach = useAlfredInOutreachEnabled();

  return useMemo(() => {
    if (!roster)
      return {
        world: null,
        known: { outreach: [], channelsIn: [], connectors: [], spaces: [] },
        drawnAgentIds: [],
        agentNames: {},
        rosterReady: false,
        items: [],
        channelsIn: [],
        connectors: [],
      };
    const parts = partitionConnectors(list ?? [], servers ?? [], {
      phoneApp,
      coders,
      alfredOnOutreach,
      instagramOutreachAvailability: instagramAvailability,
    });
    const world = buildWorld({ agents: roster.map((a) => ({ id: a.id, name: a.name })), ...parts, spaces });
    const drawnAgentIds = world.nodes.filter((n) => n.region === 'agents').map((n) => n.id.slice('agent:'.length));
    const known: KnownNodes = {
      outreach: world.nodes.filter((n) => n.region === 'outreach' && !n.folded && n.connected).map((n) => n.slug!),
      channelsIn: world.nodes.filter((n) => n.region === 'channelsIn' && !n.folded).map((n) => n.slug!),
      connectors: world.nodes.filter((n) => n.region === 'connectors' && !n.folded && !n.action).map((n) => n.slug!),
      spaces: world.nodes.filter((n) => n.region === 'spaces').map((n) => n.id.slice('space:'.length)),
    };
    const agentNames = Object.fromEntries(roster.map((a) => [a.id, a.name]));
    const items = [...parts.connectors, ...parts.channelsIn, ...parts.outreach];
    return { world, known, drawnAgentIds, agentNames, rosterReady: true, items, channelsIn: parts.channelsIn, connectors: parts.connectors };
  }, [roster, list, servers, phoneApp, coders, spaces, alfredOnOutreach, instagramAvailability]);
}
